import React from 'react';
import {
  Dashboard as DashboardIcon,
  AccessTime as TimeIcon,
  Assessment as ReportingIcon,
  AccountBalance as FinanceIcon,
  Settings as SettingsIcon,
  Domain as TenantsIcon,
  Approval as ApprovalIcon,
  CalendarMonth as CalendarIcon,
  Schedule as TimeEntriesIcon,
  AssessmentOutlined as HoursIcon,
  Receipt as ExpensesIcon,
  Description as InvoicesIcon,
  DateRange as PayPeriodsIcon,
  Business as CompaniesIcon,
  Store as AffiliatesIcon,
  People as PeopleIcon,
  Email as EmailIcon,
  History as AuditIcon,
  AccountTree as DepartmentsIcon,
  Notifications as NotificationsIcon,
  AccountBalance as DeductionsIcon,
  Payments as PayrollIcon,
  ReceiptLong as PayStubIcon,
  HealthAndSafety as BenefitsIcon,
  HowToReg as EnrollmentIcon,
  Gavel as TaxFilingIcon,
  CloudSync as ZohoIcon,
  Backup as BackupIcon,
  Timeline as PipelineIcon,
  BeachAccess as TimeOffIcon,
  HelpOutline as DocsIcon,
  Security as SecurityIcon,
  Checklist as TasksIcon,
  AssignmentTurnedIn as MyTasksIcon,
} from '@mui/icons-material';

// Single source of truth for the nav taxonomy. Consumed by the desktop
// Sidebar and the mobile MenuNavigation (bottom tabs + swipe drawer) so
// the two presentations never drift.

export const menuGroups = {
  time: {
    label: 'Time',
    icon: <TimeIcon />,
    items: ['Approvals', 'Calendar', 'Time Off', 'My Time Entries', 'Timesheets', 'Tasks', 'My Tasks'],
    payrollAdminItems: ['Approvals', 'Calendar', 'Time Off', 'My Time Entries', 'Timesheets', 'Tasks', 'My Tasks'],
    managerItems: ['Approvals', 'Calendar', 'Time Off', 'My Time Entries', 'Timesheets', 'Tasks', 'My Tasks'],
    employeeItems: ['Calendar', 'Time Off', 'My Time Entries', 'My Tasks'],
  },
  reporting: {
    label: 'Reporting',
    icon: <ReportingIcon />,
    items: ['Reports', 'Builder', 'Automation'],
    payrollAdminItems: ['Reports', 'Builder', 'Automation'],
    managerItems: ['Reports', 'Builder', 'Automation'],
  },
  finance: {
    label: 'Finance',
    icon: <FinanceIcon />,
    items: ['Expenses', 'Invoices', 'Pipeline', 'Pay Periods', 'Client Bill Rates'],
    payrollAdminItems: ['Expenses', 'Invoices', 'Pipeline', 'Pay Periods', 'Client Bill Rates'],
    managerItems: ['Expenses', 'Invoices', 'Pipeline', 'Pay Periods', 'Client Bill Rates'],
    employeeItems: ['My Expenses'],
  },
  payroll: {
    label: 'Payroll',
    icon: <PayrollIcon />,
    items: ['Deductions', 'Payroll', 'Tax Filing', 'Tax Deposits', 'YTD Import', 'My Pay'],
    payrollAdminItems: ['Deductions', 'Payroll', 'Tax Filing', 'Tax Deposits', 'YTD Import', 'My Pay'],
    managerItems: ['My Pay'],
    employeeItems: ['My Pay'],
  },
  benefits: {
    label: 'Benefits',
    icon: <BenefitsIcon />,
    items: ['Benefits', 'Enrollment', 'My Benefits'],
    payrollAdminItems: ['Benefits', 'Enrollment', 'My Benefits'],
    managerItems: ['My Benefits'],
    employeeItems: ['My Benefits'],
  },
  settings: {
    label: 'Settings',
    icon: <SettingsIcon />,
    items: ['Company Setup', 'Help', 'People', 'Roles', 'Clients', 'Affiliates', 'Departments', 'Email Settings', 'Notification Settings', 'Security', 'Shift Config', 'Holidays', 'Work Sites', 'Zoho', 'Backups', 'Transaction Log'],
    // 'Transaction Log' is listed for every elevated role; App.js only builds the
    // tab for can_view_audit_log (or program admin), and getGroupItems intersects
    // with the tabs that exist, so it shows exactly when the permission is held.
    payrollAdminItems: ['Company Setup', 'Help', 'People', 'Clients', 'Affiliates', 'Departments', 'Notification Settings', 'Holidays', 'Work Sites', 'Transaction Log'],
    managerItems: ['People', 'Clients', 'Affiliates', 'Departments', 'Notification Settings', 'Shift Config', 'Holidays', 'Work Sites', 'Help', 'Transaction Log'],
    employeeItems: ['Help'],
  },
};

// Sidebar section labels (mockup): Operations / Money / Admin
export const sidebarSections = [
  { label: 'Operations', groups: ['time', 'reporting'] },
  { label: 'Money', groups: ['finance', 'payroll', 'benefits'] },
  { label: 'Admin', groups: ['settings'] },
];

export const itemIcons = {
  'Dashboard': <DashboardIcon />,
  'Tenants': <TenantsIcon />,
  'Approvals': <ApprovalIcon />,
  'Calendar': <CalendarIcon />,
  'Time Entries': <TimeEntriesIcon />,
  'My Time Entries': <TimeEntriesIcon />,
  'Timesheets': <HoursIcon />,
  'My Hours': <HoursIcon />,
  'Expenses': <ExpensesIcon />,
  'My Expenses': <ExpensesIcon />,
  'Invoices': <InvoicesIcon />,
  'Pipeline': <PipelineIcon />,
  'Pay Periods': <PayPeriodsIcon />,
  'People': <PeopleIcon />,
  'Roles': <PeopleIcon />,
  'Company Setup': <CompaniesIcon />,
  'Clients': <CompaniesIcon />,
  'Client Bill Rates': <PayPeriodsIcon />,
  'Affiliates': <AffiliatesIcon />,
  'Departments': <DepartmentsIcon />,
  'Email Settings': <EmailIcon />,
  'Notification Settings': <NotificationsIcon />,
  'Reports': <HoursIcon />,
  'Builder': <ReportingIcon />,
  'Automation': <TimeEntriesIcon />,
  'Security': <SecurityIcon />,
  'Shift Config': <TimeEntriesIcon />,
  'Holidays': <CalendarIcon />,
  'Transaction Log': <AuditIcon />,
  'Deductions': <DeductionsIcon />,
  'Payroll': <PayrollIcon />,
  'My Pay': <PayStubIcon />,
  'Tax Filing': <TaxFilingIcon />,
  'Tax Deposits': <TaxFilingIcon />,
  'YTD Import': <TaxFilingIcon />,
  'Benefits': <BenefitsIcon />,
  'Enrollment': <EnrollmentIcon />,
  'My Benefits': <BenefitsIcon />,
  'Time Off': <TimeOffIcon />,
  'Zoho': <ZohoIcon />,
  'Backups': <BackupIcon />,
  'Help': <DocsIcon />,
  'Tasks': <TasksIcon />,
  'My Tasks': <MyTasksIcon />,
};

/**
 * Items of a nav group visible to the current user: role-specific candidate
 * list, intersected with the tabs App.js actually built (permission-filtered).
 */
export function getGroupItems(groupKey, roleFlags, availableTabLabels) {
  const group = menuGroups[groupKey];
  if (!group) return [];
  const { isAdmin, isProgramAdmin, isPayrollAdmin, isManager } = roleFlags;

  if (isAdmin || isProgramAdmin) {
    return group.items.filter(item => availableTabLabels.has(item));
  }

  let candidates;
  if (isPayrollAdmin) {
    candidates = group.payrollAdminItems || group.items;
  } else if (isManager) {
    candidates = group.managerItems || group.items;
  } else {
    // Employees and custom roles: employee list when defined, otherwise the
    // full list — the availableTabLabels intersection does the real gating.
    candidates = group.employeeItems || group.items;
  }
  return candidates.filter(item => availableTabLabels.has(item));
}
